import React from "react";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { useHotel } from "@/hooks/useHotel";
import { useHotelService } from "@/hooks/useHotelServices";
import { HotelService, HotelServiceProviderType } from "@/types/hotelService";
import {
  Camera,
  ChevronLeft,
  ChevronRight,
  Clock3,
  HandCoins,
  MapPin,
  Undo2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatWorkingDays } from "@/components/pages/services/utils/serviceSchedule";
import { formatCurrencyAmount } from "@/components/pages/services/utils/priceFormatting";

type ActivityLocationState = {
  service?: HotelService;
};

const sectionCardClassName =
  "rounded-xl bg-[#F6F6F8] py-8 px-4 grid grid-cols-12 gap-4";
const sectionTitleClassName =
  "col-span-4 self-start flex items-start gap-2 text-base leading-none font-normal text-[#443F2C]";
const sectionIconClassName = "h-4 w-4 text-[#443F2C]";
const sectionValueClassName =
  "col-span-4 self-start text-base leading-normal font-medium text-[#0A0A0A]";
const sectionMetaLabelClassName =
  "text-sm leading-normal font-normal text-[#79779F]";
const sectionMetaValueClassName =
  "text-base leading-normal font-medium text-[#0A0A0A]";
const carouselNavButtonClassName =
  "h-8 w-8 rounded-md border border-[#E5E5E5] bg-white p-0 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] hover:bg-[#FAFAFA] disabled:opacity-50 disabled:cursor-not-allowed";

export const ActivityDetailPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const { state } = useLocation() as { state: ActivityLocationState | null };
  const initialService = state?.service;

  const { hotelId, hotel } = useHotel();
  const { data, isLoading } = useHotelService(id ?? "", hotelId ?? "");
  const service = useMemo(() => initialService ?? data, [data, initialService]);

  const [carouselApi, setCarouselApi] = useState<any>(null);
  const gallery = service?.partner?.images ?? [];

  if (isLoading && !service) {
    return (
      <div className="p-12">{t("services.activity.loading", "Loading...")}</div>
    );
  }

  if (!service) {
    return (
      <div className="p-12 text-red-600">
        {t("services.activity.errors.notFound", "Activity not found.")}
      </div>
    );
  }

  const isPartnerService =
    service.providerType ===
    HotelServiceProviderType.GUESTERIA_PARTNERS_SERVICE;
  const displayCurrency = isPartnerService
    ? (service.currency ??
      service.hotel?.paymentSettings?.currency ??
      hotel?.paymentSettings?.currency)
    : (hotel?.paymentSettings?.currency ??
      service.hotel?.paymentSettings?.currency ??
      service.currency);

  return (
    <div className="mx-auto max-w-[1344px] space-y-6 p-12">
      <Button
        variant="ghost"
        onClick={() => navigate("/services")}
        className="h-8 rounded-md bg-transparent px-3 py-1 text-base font-normal text-black hover:bg-transparent"
      >
        <Undo2 className="h-6 w-6" />
        {t("services.activity.actions.back", "Back")}
      </Button>

      <div className="flex min-h-[99px] items-center justify-center gap-4">
        <div className="flex h-8 w-[80px] items-center justify-between">
          <Button
            variant="outline"
            size="icon"
            className={carouselNavButtonClassName}
            onClick={() => carouselApi?.scrollPrev()}
            disabled={!carouselApi?.canScrollPrev()}
          >
            <ChevronLeft className="h-4 w-4 text-[#3F3F46]" />
          </Button>
        </div>

        <div className="w-[476px]">
          {gallery.length ? (
            <Carousel
              opts={{ align: "start", dragFree: true }}
              setApi={setCarouselApi}
              className="w-full"
            >
              <CarouselContent className="ml-0">
                {gallery.map((image, index) => (
                  <CarouselItem
                    key={`${image.url}-${index}`}
                    className="basis-auto pl-4 first:pl-0"
                  >
                    <button
                      type="button"
                      onClick={() => carouselApi?.scrollTo(index)}
                      className="h-[99px] w-[148px] shrink-0 overflow-hidden rounded-[12px]"
                    >
                      <img
                        src={image.url}
                        alt={image.caption || service.name}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          ) : (
            <div className="flex h-[99px] w-[148px] items-center justify-center rounded-[12px] bg-slate-100">
              <Camera className="h-8 w-8 text-slate-400" />
            </div>
          )}
        </div>

        <div className="flex h-8 w-[80px] items-center justify-between">
          <Button
            variant="outline"
            size="icon"
            className={`${carouselNavButtonClassName} ml-auto`}
            onClick={() => carouselApi?.scrollNext()}
            disabled={!carouselApi?.canScrollNext()}
          >
            <ChevronRight className="h-4 w-4 text-[#3F3F46]" />
          </Button>
        </div>
      </div>

      <div className="space-y-5">
        <div className={sectionCardClassName}>
          <div className={sectionTitleClassName}>
            <MapPin className={sectionIconClassName} />
            {t("services.activity.sections.activity", "Activity")}
          </div>
          <div className={sectionValueClassName}>{service.name}</div>
          <div className="col-span-4 text-base leading-normal font-normal text-[#0A0A0A]">
            {service.description}
          </div>
        </div>

        <div className={sectionCardClassName}>
          <div className={sectionTitleClassName}>
            <HandCoins className={sectionIconClassName} />
            {t("services.activity.sections.price", "Price")}
          </div>
          <div className="col-span-4 text-sm">
            <div className={sectionMetaLabelClassName}>
              {t("services.activity.price.label", "Price")}
            </div>
            <div className={sectionMetaValueClassName}>
              {formatCurrencyAmount(
                Number(service.price ?? 0),
                displayCurrency,
              )}{" "}
              / {t("services.activity.price.perPerson", "per person")}
            </div>
          </div>
          <div className="col-span-4 text-sm">
            <div className={sectionMetaLabelClassName}>
              {t("services.activity.price.discount", "Discount")}
            </div>
            <div className={sectionMetaValueClassName}>
              {service.discount
                ? `${service.discount}%`
                : t(
                    "services.activity.price.notAvailable",
                    "not available for now",
                  )}
            </div>
          </div>
        </div>

        <div className={sectionCardClassName}>
          <div className={sectionTitleClassName}>
            <Clock3 className={sectionIconClassName} />
            {t("services.activity.sections.schedule", "Schedule")}
          </div>
          <div className="col-span-4 text-sm">
            <div className={sectionMetaLabelClassName}>
              {t("services.activity.schedule.workingDays", "Working Days")}
            </div>
            <div className={sectionMetaValueClassName}>
              {formatWorkingDays(
                service.availableDays,
                t("services.activity.common.na", "N/A"),
              )}
            </div>
          </div>
          <div className="col-span-4 text-sm">
            <div className={sectionMetaLabelClassName}>
              {t("services.activity.schedule.time", "Time")}
            </div>
            <div className={sectionMetaValueClassName}>
              {service.availableFromTime && service.availableToTime
                ? `${service.availableFromTime}-${service.availableToTime}`
                : t("services.activity.common.na", "N/A")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityDetailPage;
